import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Clock, Trash2, Loader2, CheckCircle2, XCircle, Play, Download, Smartphone, Monitor, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listVideos, deleteVideo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SalesVideoHistory from "@/components/SalesVideoHistory";
import ShortDramaHistory from "@/components/ShortDramaHistory";

const STATUS_BADGE = {
  waiting: { label: "Menunggu", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  processing: { label: "Sedang dibuat...", cls: "bg-blue-100 text-blue-700 border-blue-200", icon: Loader2 },
  completed: { label: "Video siap", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { label: "Gagal dibuat", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  // Submission outcome not confirmed: the job may exist and the credit stays held until it is checked.
  reconciling: { label: "Perlu diperiksa", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
};

export const historyBadgeKey = (v) => (v.submission_status === "submission_unknown" ? "reconciling" : v.status);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatDuration = (secs) => {
  if (!secs) return "-";
  if (secs < 60) return `${secs} dtk`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}d` : `${m} mnt`;
};

const isLandscape = (ratio) => ratio === "16:9";

const creditLabel = (v) => {
  if (v.credit_status === "finalized" && v.actual_credit != null) return `${v.actual_credit} kredit`;
  if (v.submission_status === "submission_unknown") return "Ditahan sementara";
  if (v.credit_status === "reserved") return "Reserved";
  if (v.credit_status === "released" || v.credit_status === "refunded") return "Dikembalikan";
  return null;
};

export default function VideoHistory() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null); // video object being previewed

  const load = async () => {
    setLoading(true);
    try {
      setVideos(await listVideos());
    } catch {
      toast.error("Gagal memuat riwayat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus video ini dari riwayat?")) return;
    try {
      await deleteVideo(id);
      setVideos((prev) => prev.filter((v) => v.id !== id));
      toast.success("Video dihapus");
    } catch {
      toast.error("Gagal menghapus video");
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Riwayat</p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Riwayat Video
        </h1>
        <p className="mt-2 text-slate-500 md:text-lg">Semua video yang telah Anda buat.</p>
      </div>

      {user?.id && <SalesVideoHistory key={user.id} userId={user.id} />}
      {user?.id && <ShortDramaHistory key={`shortdrama:${user.id}`} userId={user.id} />}

      <h2 className="font-heading text-xl font-semibold text-slate-900">Video Avatar</h2>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Memuat...
        </div>
      ) : videos.length === 0 ? (
        <div
          data-testid="history-empty"
          className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <History className="h-7 w-7 text-slate-400" />
          </div>
          <p className="mt-4 font-heading text-lg font-semibold text-slate-900">Belum ada video.</p>
          <p className="mt-1 text-slate-500">Video yang Anda buat akan muncul di sini.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="history-list">
          {videos.map((v) => {
            const badge = STATUS_BADGE[historyBadgeKey(v)] || STATUS_BADGE.waiting;
            const BadgeIcon = badge.icon;
            const landscape = isLandscape(v.aspect_ratio);
            const canPlay = v.status === "completed" && !!v.video_url;
            return (
              <div
                key={v.id}
                data-testid={`history-item-${v.id}`}
                className="group flex flex-col rounded-xl border border-slate-200 bg-white p-6 transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading font-semibold text-slate-900 leading-snug line-clamp-2">
                    {v.title || "Video Produk"}
                  </h3>
                  <Badge variant="outline" className={`shrink-0 gap-1 ${badge.cls}`}>
                    <BadgeIcon className={`h-3 w-3 ${v.status === "processing" ? "animate-spin" : ""}`} />
                    {badge.label}
                  </Badge>
                </div>

                <p className="mt-2 text-sm text-slate-500 line-clamp-2">{v.script || "—"}</p>

                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1" data-testid={`history-duration-${v.id}`}>
                    <Clock className="h-3.5 w-3.5" /> {formatDuration(v.estimated_duration)}
                  </span>
                  <span className="inline-flex items-center gap-1" data-testid={`history-format-${v.id}`}>
                    {landscape ? <Monitor className="h-3.5 w-3.5" /> : <Smartphone className="h-3.5 w-3.5" />}
                    {landscape ? "16:9 Landscape" : "9:16 Vertikal"}
                  </span>
                  {creditLabel(v) && (
                    <span className="inline-flex items-center gap-1" data-testid={`history-credit-${v.id}`}>
                      <Coins className="h-3.5 w-3.5" /> {creditLabel(v)}
                    </span>
                  )}
                </div>

                <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-3">
                  {canPlay ? (
                    <Button
                      size="sm"
                      data-testid={`play-video-${v.id}`}
                      onClick={() => setPlaying(v)}
                      className="bg-blue-600 hover:bg-blue-700 text-white active:scale-95 transition-transform"
                    >
                      <Play className="h-4 w-4 mr-1.5" /> Play
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400">{badge.label}</span>
                  )}
                  {canPlay && (
                    <a
                      href={v.video_url}
                      target="_blank"
                      rel="noreferrer"
                      download
                      data-testid={`download-video-${v.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors active:scale-95"
                    >
                      <Download className="h-4 w-4" /> Download
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`delete-video-${v.id}`}
                    onClick={() => handleDelete(v.id)}
                    className="ml-auto text-slate-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <p className="mt-3 text-xs text-slate-400">{formatDate(v.created_at)}</p>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!playing} onOpenChange={(o) => !o && setPlaying(null)}>
        <DialogContent className="max-w-md" data-testid="history-player-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{playing?.title || "Video Produk"}</DialogTitle>
          </DialogHeader>
          {playing?.video_url && (
            <div
              className={`mx-auto w-full overflow-hidden rounded-xl bg-slate-900 ${
                isLandscape(playing.aspect_ratio) ? "aspect-video max-w-md" : "aspect-[9/16] max-w-[260px]"
              }`}
            >
              <video
                data-testid="history-video-player"
                src={playing.video_url}
                controls
                autoPlay
                playsInline
                className="h-full w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
